import React, { useEffect, useRef, useState } from 'react'
import { track } from '../../analytics'
import type { ServerApiClient } from '../../data/ServerApiClient'
import { unlockDemoGate } from '../../data/demoSession'
import './CfoDemoLeadGate.css'

const PAGE_LABELS: Record<string, string> = {
  financials: 'Financials',
  ratios: 'Ratios & trends',
  ar: 'A/R & collectability',
  'gl-detail': 'GL detail',
  explore: 'Viz & sheets',
  cash: '13-week cash',
  forecast: 'Forecast & budget',
  scenarios: 'Scenarios',
  valuation: 'Valuation',
  presentations: 'Presentations',
  'analysis-profile': 'AI tuning',
}

const pageLabel = (page: string): string => PAGE_LABELS[page] || 'the next module'

export type CfoDemoLeadGateProps = {
  api: ServerApiClient
  /** Module the visitor tried to open when the gate appeared. */
  gatedPage: string
  /** Modules already explored inside the free allowance. */
  visitedPages: string[]
  onUnlocked: () => void
  onBack: () => void
  /** Where this capture came from; the CRM reads it. Defaults to the gate. */
  source?: 'demo_gate' | 'tour'
  /**
   * Copy overrides for a tour's `lead_form` step.
   *
   * The schema REQUIRES title and body on a lead_form widget, but the tour
   * layer swapped the whole card for this component and passed neither — so
   * every word an author wrote on a lead step was dead, silently. Authors had
   * no way to tell: the gate rendered perfectly, just never with their copy.
   * Omitted means the module-gate wording below, which is what the
   * three-module ambush should keep saying.
   */
  headline?: string | null
  subhead?: string | null
  submitLabel?: string | null
}

/**
 * Full-screen lead gate shown when a demo visitor opens a fourth CFO module.
 * Contact info unlocks the rest of the demo for this browser session and
 * triggers the follow-up email (demo link + sample Excel workbook). The
 * secondary action returns to the modules already opened — nothing is lost.
 */
export function CfoDemoLeadGate({
  api,
  gatedPage,
  visitedPages,
  onUnlocked,
  onBack,
  source = 'demo_gate',
  headline,
  subhead,
  submitLabel,
}: CfoDemoLeadGateProps) {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [firmName, setFirmName] = useState('')
  /**
   * Phone was the fourth field and the least useful: nobody types a real number
   * to unlock a demo, and the field costs completions. This replaces it with
   * the one answer that changes what the lead is WORTH — a restaurant owner and
   * a firm with twelve restaurant clients look identical without it, and differ
   * by roughly 20x in contract value.
   */
  const [scale, setScale] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const shownTracked = useRef(false)

  useEffect(() => {
    if (shownTracked.current) return
    shownTracked.current = true
    track({
      event: 'demo.gate_shown',
      props: { gated_page: gatedPage, pages_viewed: visitedPages },
    })
  }, [gatedPage, visitedPages])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const normalizedEmail = email.trim()
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      setError('Please enter a valid work email.')
      return
    }
    setSubmitting(true)
    try {
      await api.submitDemoLead({
        email: normalizedEmail,
        full_name: fullName.trim() || null,
        firm_name: firmName.trim() || null,
        // Carried in the firm slot until the server has its own column.
        phone: null,
        scale: scale || null,
        pages_viewed: [...visitedPages, gatedPage],
        source,
      })
      unlockDemoGate()
      track({
        event: 'demo.gate_unlocked',
        props: { gated_page: gatedPage, pages_viewed: visitedPages },
      })
      onUnlocked()
    } catch (err: any) {
      setSubmitting(false)
      setError(String(err?.message || err || 'Something went wrong. Please try again.'))
    }
  }

  const goBack = () => {
    track({
      event: 'demo.gate_declined',
      props: { gated_page: gatedPage, pages_viewed: visitedPages },
    })
    onBack()
  }

  return (
    <div className="cfo-demo-gate" role="dialog" aria-modal="true" aria-labelledby="cfo-demo-gate-title">
      <form className="cfo-demo-gate__card" onSubmit={submit}>
        <div className="cfo-demo-gate__eyebrow">Live demo</div>
        <h2 id="cfo-demo-gate-title">
          {headline || `That\u2019s ${visitedPages.length} modules explored — want the rest?`}
        </h2>
        <p className="cfo-demo-gate__sub">
          {subhead || (
            <>
              You were headed to <strong>{pageLabel(gatedPage)}</strong>. Leave your email and keep
              going: every module unlocks, and we&rsquo;ll email you a link back into the demo so you
              can pick up where you left off.
            </>
          )}
        </p>
        {error && <div className="cfo-demo-gate__error">{error}</div>}
        <div className="cfo-demo-gate__field">
          <label htmlFor="demo-gate-email">Work email</label>
          <input
            id="demo-gate-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder="jane@yourfirm.com"
            autoFocus
          />
        </div>
        <div className="cfo-demo-gate__row">
          <div className="cfo-demo-gate__field">
            <label htmlFor="demo-gate-name">
              Name <span className="cfo-demo-gate__optional">(optional)</span>
            </label>
            <input
              id="demo-gate-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoComplete="name"
              placeholder="Jane Doe"
            />
          </div>
          <div className="cfo-demo-gate__field">
            <label htmlFor="demo-gate-firm">
              Firm or company <span className="cfo-demo-gate__optional">(optional)</span>
            </label>
            <input
              id="demo-gate-firm"
              value={firmName}
              onChange={(e) => setFirmName(e.target.value)}
              autoComplete="organization"
              placeholder="Acme Advisory"
            />
          </div>
        </div>
        <div className="cfo-demo-gate__field">
          <label htmlFor="demo-gate-scale">
            Clients you&rsquo;d put on FuseGrid <span className="cfo-demo-gate__optional">(optional)</span>
          </label>
          <select
            id="demo-gate-scale"
            value={scale}
            onChange={(e) => setScale(e.target.value)}
          >
            <option value="">Select…</option>
            <option value="1-5">1–5</option>
            <option value="6-20">6–20</option>
            <option value="21-50">21–50</option>
            <option value="50+">More than 50</option>
            <option value="not-a-firm">Not a firm — this is for my own business</option>
          </select>
        </div>
        <button type="submit" className="cfo-demo-gate__submit" disabled={submitting}>
          {submitting ? 'Unlocking…' : submitLabel || 'Unlock the full demo →'}
        </button>
        <button type="button" className="cfo-demo-gate__back" onClick={goBack}>
          ← Stick with the modules I&rsquo;ve opened
        </button>
        <p className="cfo-demo-gate__fineprint">
          No account, no card. We&rsquo;ll send one follow-up email and won&rsquo;t share your
          address.
        </p>
      </form>
    </div>
  )
}
